import { Endpoints } from '../api';
import { setAccessToken, setRefreshToken } from '../redux/slices/user';
import { store } from '../redux/store';

/**
 * Single in-flight refresh: concurrent 401s await the same promise instead of
 * the old pattern (`isRefreshing ? return ''`) which logged users out.
 */
let refreshPromise: Promise<string | null> | null = null;

type RefreshTokenResponse = {
    accessToken?: string;
    refreshToken?: string;
};

async function performRefresh(accessToken: string, refreshToken: string): Promise<string | null> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        };

        const res = await fetch(`${Endpoints.authUrl}/auth/refresh-token`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ RefreshToken: refreshToken }),
            credentials: 'include'
        });

        if (!res.ok) {
            return null;
        }

        const data = (await res.json()) as RefreshTokenResponse;
        const newAccess = data.accessToken;

        if (typeof newAccess !== 'string' || !newAccess.trim()) {
            return null;
        }

        if (typeof data.refreshToken === 'string' && data.refreshToken.length > 0) {
            store.dispatch(setRefreshToken(data.refreshToken));
        }
        store.dispatch(setAccessToken(newAccess));

        return newAccess;
    } catch (err) {
        console.error('Token refresh failed:', err);
        return null;
    }
}

/**
 * Calls `POST …/auth/refresh-token` with `{ RefreshToken }`, updates Redux
 * (access + optional rotated refresh), returns new access token or null.
 */
export function refreshAccessToken(accessToken: string, refreshToken: string): Promise<string | null> {
    if (!refreshPromise) {
        refreshPromise = performRefresh(accessToken, refreshToken).finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}
